/**
 * Nudging overlapping ground models apart.
 *
 * Cam, 2026-09-07: "Can you tell when a model is in contact when another one and put a gap in? right
 * now Cam and his turret are overlapping. If we leave the circle markers in the same spot but move
 * the models. I think that would look good."
 *
 * So this moves the MODEL only. The marker layers keep drawing at the true position, which is what
 * keeps the map honest: a site's disc still marks where the site actually is, and the offset never
 * exceeds a model's own radius, so the character stays visibly on its own marker.
 *
 * Sites are built at least 100 m apart and a model is ~80 m across, so a player standing on their
 * own turret is the common case rather than an edge case.
 *
 * Pure, metre-space, and free of three.js and MapLibre so it can be tested directly.
 *
 * ⚠️ Must be DETERMINISTIC for a given input. It runs on every camera move, and a result that
 * depended on iteration order or on a random seed would make the models twitch as you pan.
 */

export interface Placeable {
  id: number;
  lat: number;
  lon: number;
  /** Ground radius of this model in metres. */
  radiusM: number;
}

const M_PER_DEG_LAT = 111_320;
const mPerDegLon = (lat: number) => Math.max(1, M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180));

/**
 * Positions with overlaps relaxed away. Input order is preserved; input is not mutated.
 *
 * A few passes of pairwise push is plenty — these are handfuls of models within a few hundred
 * metres, not a physics sim, and stopping early leaves a near-touch rather than a jump.
 */
export function separated(items: Placeable[], passes = 6): { lat: number; lon: number }[] {
  const n = items.length;
  const out = items.map((i) => ({ lat: i.lat, lon: i.lon }));
  if (n < 2) return out;

  // Work in metres on a local tangent plane. One reference latitude for the whole set keeps the
  // east/north scale consistent, so a push is symmetric instead of stretching with latitude.
  const refLat = items.reduce((a, i) => a + i.lat, 0) / n;
  const mLon = mPerDegLon(refLat);
  const x = out.map((p) => p.lon * mLon);
  const y = out.map((p) => p.lat * M_PER_DEG_LAT);

  for (let pass = 0; pass < passes; pass++) {
    let moved = false;
    for (let a = 0; a < n; a++) {
      for (let b = a + 1; b < n; b++) {
        const want = items[a].radiusM + items[b].radiusM;
        let dx = x[b] - x[a], dy = y[b] - y[a];
        let d = Math.hypot(dx, dy);
        if (d >= want) continue;
        if (d < 1e-6) {
          // Exactly coincident: no direction to push along. Derive one from the ids so the same
          // pair always separates the same way — a random angle would twitch on every camera move.
          const h = ((items[a].id * 2654435761) ^ (items[b].id * 40503)) >>> 0;
          const ang = (h % 3600) / 3600 * 2 * Math.PI;
          dx = Math.cos(ang); dy = Math.sin(ang); d = 1e-6;
        }
        const push = (want - d) / 2;
        const ux = dx / d, uy = dy / d;
        x[a] -= ux * push; y[a] -= uy * push;
        x[b] += ux * push; y[b] += uy * push;
        moved = true;
      }
    }
    if (!moved) break; // already clear — do not keep nudging a settled arrangement
  }

  // ⚠️ CLAMP, and this is the part that matters more than the relaxation.
  //
  // Cam's constraint was "leave the circle markers in the same spot but move the models", so a model
  // that drifts off its own marker makes the map lie about where a site is. Relaxation alone does
  // not respect that: ten models on one spot pushed the outermost 143 m away, well off its disc.
  //
  // So each model may move at most its own radius. A dense pile-up therefore stays slightly
  // overlapped instead of exploding outward, which is the right way round — the pair Cam actually
  // has (a player on their own turret) resolves completely, and a crowd stays legible and honest.
  for (let i = 0; i < n; i++) {
    const dx = x[i] - items[i].lon * mLon, dy = y[i] - items[i].lat * M_PER_DEG_LAT;
    if (dx === 0 && dy === 0) { out[i] = { lat: items[i].lat, lon: items[i].lon }; continue; } // exact, not round-tripped through metres
    const d = Math.hypot(dx, dy);
    const max = items[i].radiusM;
    const [fx, fy] = d > max ? [dx * (max / d), dy * (max / d)] : [dx, dy];
    out[i] = { lat: (items[i].lat * M_PER_DEG_LAT + fy) / M_PER_DEG_LAT, lon: (items[i].lon * mLon + fx) / mLon };
  }
  return out;
}
